import React, { memo, useEffect, useMemo } from 'react';

import { useToggleState } from '@garnish/core';
import { useSelector } from '@xstate/react';

import { useLocationSearchFlags } from '../../../../../../hooks/useLocationSearchFlags';
import { useLocationSearchVisibleLocations } from '../../../../../../hooks/useLocationSearchVisibleLocations';
import { type LocationResultsListsV2Props } from '../../LocationResultsListsV2.types';
import { LocationResultsListsV2Collapsed, LocationResultsListsV2Expanded } from './components';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationResultsListsV2XS = memo((props: LocationResultsListsV2Props) => {
  const {
    locationSearchActorRef,
    variation,
    isExpandedByDefault,
    withSafeAreaInsets,
    onExpandedChange,
    interactedLocationRestaurantSlug,
    interactedLocationDeliveryAddressId,
  } = props;

  const { isUsingSearchField } = useLocationSearchFlags({ locationSearchActorRef });
  const visibleLocations = useLocationSearchVisibleLocations({ locationSearchActorRef });

  // ─── State ───────────────────────────────────────────────────────────

  const {
    value: isExpanded,
    toggleOn: expand,
    toggleOff: collapse,
  } = useToggleState(isExpandedByDefault || isUsingSearchField);

  // ─── Derived ─────────────────────────────────────────────────────────

  const selectedLocationSearchType = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.locationSearchType,
  );
  const recentLocations = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.recentLocations,
  );
  const nearbyLocations = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.nearbyLocations,
  );
  const focusedLocationId = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.focusedLocationId,
  );
  const deliveryLocation = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.deliveryLocation,
  );

  const allLocations = useMemo(() => {
    const { pickup = [], outpost = [] } = visibleLocations ?? {};
    const nearbyPickupLocations = nearbyLocations?.pickup ?? [];
    const nearbyOutpostLocations = nearbyLocations?.outpost ?? [];

    if (variation === 'recent-and-nearby') {
      return [
        ...(recentLocations ?? []),
        ...(selectedLocationSearchType === 'pickup' ? nearbyPickupLocations : []),
        ...(selectedLocationSearchType === 'outpost' ? nearbyOutpostLocations : []),
      ];
    }

    if (selectedLocationSearchType === 'delivery') {
      return deliveryLocation ? [deliveryLocation as unknown as (typeof pickup)[number]] : [];
    }

    return selectedLocationSearchType === 'outpost' ? outpost : pickup;
  }, [
    variation,
    deliveryLocation,
    visibleLocations,
    recentLocations,
    nearbyLocations,
    selectedLocationSearchType,
  ]);

  const canBeExpanded = allLocations.length > 1 && selectedLocationSearchType !== 'delivery';

  const focusedLocation =
    allLocations.find((location) => location.id === focusedLocationId) ?? allLocations[0];

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    onExpandedChange?.(isExpanded);
  }, [isExpanded, onExpandedChange]);

  useEffect(() => {
    return () => {
      onExpandedChange?.(true);
    };
  }, [onExpandedChange]);

  // Expand when the user searches for locations in the search field.
  useEffect(() => {
    if (!isUsingSearchField) return;

    expand();
  }, [expand, isUsingSearchField]);

  // ─────────────────────────────────────────────────────────────────────

  if (isExpanded) {
    return (
      <LocationResultsListsV2Expanded
        locationSearchActorRef={locationSearchActorRef}
        variation={variation}
        interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
        interactedLocationDeliveryAddressId={interactedLocationDeliveryAddressId}
        onCollapse={collapse}
      />
    );
  }

  return (
    <LocationResultsListsV2Collapsed
      locationSearchActorRef={locationSearchActorRef}
      variation={variation}
      focusedLocation={focusedLocation}
      canBeExpanded={canBeExpanded}
      withSafeAreaInsets={withSafeAreaInsets}
      interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
      interactedLocationDeliveryAddressId={interactedLocationDeliveryAddressId}
      onExpand={expand}
    />
  );
});
